import React, { useState, useEffect } from 'react';
import { AlertTriangle, Mail, Plus, RefreshCw, Trash2, Users, Gauge } from 'lucide-react';
import { api } from '../api';
import { useTranslation } from 'react-i18next';

const TrainerManagement = ({ user }) => {
  const { t } = useTranslation();
  const [trainers, setTrainers] = useState([]);
  const [setupStatus, setSetupStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [newTrainerEmail, setNewTrainerEmail] = useState('');
  const [newTrainerName, setNewTrainerName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [orgCapacity, setOrgCapacity] = useState(null); // { has_cap, max_users, seats_used, seats_remaining, rule_name }

  const fetchTrainers = async () => {
    try {
      const data = await api.request('/partner-admin/setup-status');
      setSetupStatus(data);
      setTrainers(data.designated_trainers || []);
    } catch (err) {
      console.error('Failed to load trainers:', err);
      setError('Failed to load trainers');
    }
    setLoading(false);
  };

  const fetchOrgCapacity = async () => {
    try {
      const data = await api.request('/partner-admin/org-capacity');
      setOrgCapacity(data);
    } catch (err) {
      console.error('Failed to load organization capacity:', err);
    }
  };

  useEffect(() => {
    fetchTrainers();
    fetchOrgCapacity();
  }, []);

  const resetModal = () => {
    setNewTrainerEmail('');
    setNewTrainerName('');
    setError('');
  };

  const handleInviteTrainer = async () => {
    if (!newTrainerEmail.trim()) {
      setError('Please enter an email address');
      return;
    }
    setInviting(true);
    setError('');
    try {
      await api.request('/partner-admin/invite-trainer', {
        method: 'POST',
        body: JSON.stringify({
          email: newTrainerEmail.trim(),
          full_name: newTrainerName.trim() || null
        })
      });
      setSuccess(`${t('trainerManagement.invitationSent')} ${newTrainerEmail.trim()}! ${t('trainerManagement.invitationSentSuffix')}`);
      setShowAddModal(false);
      resetModal();
      fetchTrainers();
      fetchOrgCapacity();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message);
    }
    setInviting(false);
  };

  const handleResendInvitation = async (trainer) => {
    setResendingId(trainer.id);
    try {
      await api.request(`/partner-admin/resend-invitation/${encodeURIComponent(trainer.email)}`, {
        method: 'POST'
      });
      setSuccess(`${t('trainerManagement.invitationResent')} ${trainer.email}`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message);
    }
    setResendingId(null);
  };

  const handleDeleteTrainer = async () => {
    setSubmitting(true);
    setError('');
    try {
      await api.request(`/partner-admin/trainer/${encodeURIComponent(selectedTrainer.email)}`, {
        method: 'DELETE'
      });
      setSuccess(t('trainerManagement.trainerRemoved'));
      setShowDeleteModal(false);
      setSelectedTrainer(null);
      fetchTrainers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  const openDeleteModal = (trainer) => {
    setSelectedTrainer(trainer);
    setShowDeleteModal(true);
    setError('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-12 h-12 border-4 border-levelup-accent border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="trainer-management">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-levelup-dark">Trainer Management</h1>
          <p className="text-gray-600">
            Manage designated trainers for <span className="font-semibold">{user.domain}</span>
          </p>
        </div>
        <div className="text-right">
          {orgCapacity?.has_cap ? (
            <>
              <span
                data-testid="org-capacity-badge"
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${
                  orgCapacity.seats_remaining <= 0 ? 'bg-red-100 text-red-700' : 'bg-indigo-100 text-indigo-700'
                }`}
              >
                <Gauge size={14} /> {orgCapacity.seats_used} / {orgCapacity.max_users} org seats used
              </span>
              <p className="text-xs text-gray-500 mt-1" data-testid="org-capacity-breakdown">
                {orgCapacity.admins_count} Admin{orgCapacity.admins_count !== 1 ? 's' : ''} / {orgCapacity.trainers_count} Trainer{orgCapacity.trainers_count !== 1 ? 's' : ''}
              </p>
            </>
          ) : (
            <span className="text-sm text-gray-500">Unlimited organization capacity</span>
          )}
        </div>
      </div>

      {orgCapacity?.has_cap && orgCapacity.seats_remaining <= 0 && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-start gap-2" data-testid="org-capacity-full-banner">
          <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
          <p className="text-sm">
            Your organization has reached its access limit of <strong>{orgCapacity.max_users} users</strong> on Skilling in a Box.
            You can still send invitations, but newly invited people won't be able to log in until a seat is freed up.
            {orgCapacity.waitlist_count > 0 && (
              <> <strong>{orgCapacity.waitlist_count}</strong> {orgCapacity.waitlist_count === 1 ? 'person is' : 'people are'} currently waitlisted and will be automatically let in the moment a seat opens up.</>
            )}
            {' '}Contact your Skilling in a Box administrator to increase capacity.
          </p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg mb-6">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded-lg mb-6">
          {success}
        </div>
      )}

      {/* Add Trainer Button */}
      <div className="mb-6">
        <button
          onClick={() => { setShowAddModal(true); resetModal(); }}
          className="flex items-center bg-levelup-accent text-levelup-darker px-6 py-3 rounded-lg font-semibold hover:bg-levelup-accent-hover transition-all"
          data-testid="add-trainer-btn"
        >
          <Plus size={20} className="mr-2" />
          {t('trainerManagement.addTrainer')}
        </button>
      </div>

      {/* Trainers List */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-4 bg-levelup-dark text-white">
          <h2 className="font-bold">{t('trainerManagement.designatedTrainers')}</h2>
        </div>

        {/* Admin (self) - cannot be removed */}
        <div className="p-4 border-b bg-levelup-light">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-full bg-levelup-accent flex items-center justify-center text-levelup-darker font-bold text-lg mr-4">
                {user.full_name?.charAt(0) || 'A'}
              </div>
              <div>
                <p className="font-medium text-levelup-dark">{user.email}</p>
                <p className="text-sm text-gray-600">{user.full_name}</p>
              </div>
            </div>
            <span className="px-4 py-2 bg-levelup-accent text-levelup-darker rounded-full text-sm font-medium">
              {t('trainerManagement.partnerAdmin')}
            </span>
          </div>
        </div>

        {/* Other trainers */}
        {trainers.map((trainer) => (
          <div key={trainer.id} className="p-4 border-b hover:bg-gray-50 transition-colors" data-testid={`trainer-${trainer.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-bold text-lg mr-4">
                  {trainer.full_name?.charAt(0) || trainer.email.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-medium text-levelup-dark">{trainer.email}</p>
                  <p className="text-sm text-gray-600">{trainer.full_name || t('trainerManagement.noNameProvided')}</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                {trainer.role === 'partner_admin' && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-levelup-accent/20 text-levelup-dark" data-testid={`role-badge-${trainer.id}`}>
                    {t('trainerManagement.partnerAdminRole')}
                  </span>
                )}
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  trainer.pending_registration ? 'bg-blue-100 text-blue-700' :
                  trainer.is_approved ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {trainer.pending_registration ? t('trainerManagement.invited') : trainer.is_approved ? t('trainerManagement.active') : t('trainerManagement.pending')}
                </span>
                {trainer.pending_registration && (
                  <button
                    onClick={() => handleResendInvitation(trainer)}
                    disabled={resendingId === trainer.id}
                    className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Resend invitation email"
                    data-testid={`resend-invite-${trainer.id}`}
                  >
                    <RefreshCw size={18} className={resendingId === trainer.id ? 'animate-spin' : ''} />
                  </button>
                )}
                {trainer.role !== 'partner_admin' && (
                  <button
                    onClick={() => openDeleteModal(trainer)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title={t('trainerManagement.removeTrainerTitle')}
                    data-testid={`delete-trainer-${trainer.id}`}
                  >
                    <Trash2 size={20} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        {trainers.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <Users size={48} className="mx-auto mb-4 text-gray-300" />
            <p>{t('trainerManagement.noTrainers')}</p>
            <p className="text-sm">{t('trainerManagement.noTrainersHint')}</p>
          </div>
        )}
      </div>

      {/* Add Trainer Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" data-testid="add-trainer-modal">
            <div className="p-6 border-b">
              <h3 className="text-xl font-bold text-levelup-dark">{t('trainerManagement.addTrainerModalTitle')}</h3>
              <p className="text-sm text-gray-600 mt-1">
                {t('trainerManagement.addTrainerModalSubtitle')} ({user.domain})
              </p>
            </div>

            <div className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {orgCapacity?.has_cap && (
                <div className={`flex items-start gap-2 px-3 py-2 rounded-lg text-xs ${
                  orgCapacity.seats_remaining <= 0 ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-indigo-50 border border-indigo-100 text-indigo-700'
                }`} data-testid="invite-capacity-hint">
                  <Gauge size={14} className="flex-shrink-0 mt-0.5" />
                  {orgCapacity.seats_remaining <= 0 ? (
                    <span>Your organization has <strong>0 seats left</strong> out of {orgCapacity.max_users}. This invite may not be able to log in until a seat is freed.</span>
                  ) : (
                    <span>Your organization has <strong>{orgCapacity.seats_remaining} of {orgCapacity.max_users}</strong> seats remaining.</span>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={newTrainerEmail}
                  onChange={(e) => { setNewTrainerEmail(e.target.value); setError(''); }}
                  placeholder={`trainer@${user.domain}`}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-levelup-accent focus:border-transparent"
                  data-testid="new-trainer-email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('trainerManagement.fullNameOptional')}
                </label>
                <input
                  type="text"
                  value={newTrainerName}
                  onChange={(e) => setNewTrainerName(e.target.value)}
                  placeholder={t('trainerManagement.fullNamePlaceholder')}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-levelup-accent focus:border-transparent"
                  data-testid="new-trainer-name"
                />
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex justify-between items-center">
              <button
                onClick={() => { setShowAddModal(false); resetModal(); }}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                data-testid="cancel-add-trainer"
              >
                {t('trainerManagement.cancel')}
              </button>

              <button
                onClick={handleInviteTrainer}
                disabled={inviting || !newTrainerEmail.trim()}
                className="flex items-center bg-levelup-accent text-levelup-darker px-5 py-2 rounded-lg font-semibold hover:bg-levelup-accent-hover transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="send-invitation-btn"
              >
                <Mail size={16} className="mr-2" />
                {inviting ? t('trainerManagement.sending') : t('trainerManagement.sendInvitation')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedTrainer && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" data-testid="delete-trainer-modal">
            <div className="p-6 border-b">
              <div className="flex items-center text-red-600">
                <AlertTriangle size={24} className="mr-2" />
                <h3 className="text-xl font-bold">{t('trainerManagement.removeTrainerModalTitle')}</h3>
              </div>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-4">
                {t('trainerManagement.removeConfirm')} <span className="font-semibold">{selectedTrainer.email}</span> {t('trainerManagement.removeConfirmSuffix')}
              </p>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800 text-sm font-medium mb-2">{t('trainerManagement.importantNotice')}</p>
                <ul className="text-yellow-700 text-sm space-y-1">
                  <li>• {t('trainerManagement.removeNotice1')}</li>
                  <li>• {t('trainerManagement.removeNotice2')}</li>
                  <li>• {t('trainerManagement.removeNotice3')}</li>
                  <li>• {t('trainerManagement.removeNotice4')}</li>
                </ul>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={() => { setShowDeleteModal(false); setSelectedTrainer(null); setError(''); }}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                {t('trainerManagement.cancel')}
              </button>
              <button
                onClick={handleDeleteTrainer}
                disabled={submitting}
                className="bg-red-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-700 transition-all disabled:opacity-50"
                data-testid="confirm-delete-trainer"
              >
                {submitting ? t('trainerManagement.removing') : t('trainerManagement.removeTrainerModalTitle')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Server Info Page Component (for firewall configuration)

export default TrainerManagement;
